/* eslint-disable @typescript-eslint/no-unused-vars */
import { AppBar, Box, Dialog, IconButton, Toolbar } from '@mui/material'
import { IconClose } from 'assets/images'
import { COLUMNS } from 'constants/common'
import { StatisticPageContext } from 'context/StatisticPageContext'
import { useImageFit } from 'hooks/useImageFit'
import { t } from 'i18n'
import { isEmpty } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
// We no longer fetch individual reports; coordinates are embedded in statistics response
import { ApplicationState } from 'store'
import { Coordinate, Resort, Seasons } from 'store/ducks'
import * as ResortActions from 'store/ducks/resort/actions'
import styled from 'styled-components'
import {
  FullImgPreview,
  NoImageBox,
  SmallImgPreview,
  StyledLoading
} from '../Slopes/styled'

interface Props {
  resort: Resort
  getResortRequest: (id: string | undefined) => void
  season: string
  isLoading: boolean
}

const ImageWrapper = styled(Box)<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  max-height: 100%;
  background-color: #eeeeee;
  margin-bottom: 2rem;
  position: relative;
`

const StyledCircle = styled.div<{
  $circle: number
  $top: number
  $left: number
}>`
  z-index: 1;
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $circle }) => $circle}px;
  height: ${({ $circle }) => $circle}px;
  background-color: red;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  display: flex;
  font-size: 12px;
  color: white;
`

const RenderCoordinates = ({
  coordinates,
  cellSize,
  flag = false
}: {
  coordinates: Coordinate[]
  cellSize: number
  flag?: boolean
}) => {
  const uniqueCoordinates = coordinates.filter(
    (item, index, self) =>
      index === self.findIndex(t => t.x === item.x && t.y === item.y)
  )

  if (isEmpty(coordinates)) return null
  return (
    <>
      {uniqueCoordinates.map((coordinate, index) => {
        const itemToFind = { x: coordinate.x, y: coordinate.y }
        // Allow 0 values; only guard against null/undefined
        if (itemToFind.x == null || itemToFind.y == null) return null
        const sameItems = coordinates.filter(
          item => item.x === itemToFind.x && item.y === itemToFind.y
        )
        const additionalPixels = sameItems.length * 3
        const circle = cellSize + additionalPixels
        const top = coordinate.y * cellSize - additionalPixels / 2
        const left = coordinate.x * cellSize - additionalPixels / 2
        return (
          <StyledCircle key={index} $circle={circle} $top={top} $left={left}>
            {flag && sameItems.length}
          </StyledCircle>
        )
      })}
    </>
  )
}

const ScrollRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 8px;
`

const Panel = styled.div`
  flex: 0 0 auto;
`

const Heatmap = ({ resort, getResortRequest, season, isLoading }: Props) => {
  const [preview, setPreview] = useState('')
  const [loading, setLoading] = useState(false)
  const [dialogState, setDialogState] = useState<{
    open: boolean
    url: string
    coordinates: Coordinate[]
    name: string
  }>({
    open: false,
    url: '',
    coordinates: [],
    name: ''
  })
  const { statistics, filterData } = useContext(StatisticPageContext)
  const [coordsByHeatmap, setCoordsByHeatmap] = useState<
    Record<string, Coordinate[]>
  >({})

  const targetSeason =
    season === Seasons.WINTER.toLowerCase() ? Seasons.WINTER : Seasons.SUMMER

  const seasonHeatmaps = useMemo(
    () => statistics[targetSeason].heatmaps || [],
    [statistics, targetSeason]
  )
  const defaultHeatmap = useMemo(
    () => seasonHeatmaps.find(h => h.isDefault),
    [seasonHeatmaps]
  )
  const selectedUrl = useMemo(
    () => defaultHeatmap?.url || seasonHeatmaps[0]?.url || '',
    [defaultHeatmap, seasonHeatmaps]
  )

  const { displayWidth, displayHeight, imgLoading } = useImageFit(
    dialogState.open ? dialogState.url : preview || selectedUrl,
    dialogState.open,
    {
      thumbnailMaxWidth: 450,
      thumbnailMaxHeight: 450
    }
  )

  const handleClickOpen = (url: string, coords: Coordinate[], name: string) => {
    setDialogState({
      open: true,
      url,
      coordinates: coords,
      name
    })
  }

  const handleClose = () => {
    setDialogState({
      open: false,
      url: '',
      coordinates: [],
      name: ''
    })
  }

  useEffect(() => {
    setCoordsByHeatmap(statistics[targetSeason]?.coordinatesByHeatmap || {})
  }, [statistics, targetSeason])

  useEffect(() => {
    if (selectedUrl) {
      setLoading(true)
      setPreview(selectedUrl)
    } else {
      setPreview('')
      setLoading(false)
    }
  }, [selectedUrl])

  // Removed handleUpload and its useEffect

  // Map groups to season heatmaps with metadata and sort by count desc
  const panels = useMemo(() => {
    const entries = Object.entries(coordsByHeatmap)
      .map(([hid, coords]) => {
        const hm = seasonHeatmaps.find(h => h.id === hid)
        return {
          heatmap: hm,
          heatmapId: hid,
          count: coords.length,
          coords
        }
      })
      .filter(p => p.count > 0)
      .sort((a, b) => b.count - a.count)
    return entries
  }, [coordsByHeatmap, seasonHeatmaps])

  const cellSize = displayWidth / COLUMNS

  if (filterData.resort === 'ALL') return null
  return (
    <div>
      <Dialog open={dialogState.open} fullScreen>
        <AppBar
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '2rem'
          }}
        >
          <span>{dialogState.name || t('heatmap', { season: t(season) })}</span>
          <Toolbar>
            <IconButton
              autoFocus
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <IconClose />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div
          style={{
            display: 'flex',
            margin: '0 auto',
            position: 'relative',
            width: displayWidth,
            height: displayHeight
          }}
        >
          <FullImgPreview
            src={dialogState.url}
            alt={t('preview')}
            style={{ width: displayWidth, height: displayHeight }}
          />
          <RenderCoordinates
            coordinates={dialogState.coordinates}
            cellSize={displayWidth / COLUMNS}
            flag
          />
        </div>
      </Dialog>

      {/* <h4>{t('heatmap', { season: t(season) })}</h4> */}
      <ScrollRow>
        {panels.length === 0 && (
          <ImageWrapper $width={displayWidth}>
            {(loading || isLoading || imgLoading) && <StyledLoading />}
            {!preview ? (
              <NoImageBox>
                <span>{t('noImage')}</span>
              </NoImageBox>
            ) : (
              <div
                onClick={() =>
                  handleClickOpen(
                    preview,
                    [],
                    t('heatmap', { season: t(season) })
                  )
                }
                style={{ cursor: 'pointer' }}
              >
                <SmallImgPreview
                  src={preview}
                  alt={t('preview')}
                  style={{ width: displayWidth, height: displayHeight }}
                />
              </div>
            )}
          </ImageWrapper>
        )}
        {panels.map(({ heatmap: hm, heatmapId, coords, count }, idx) => {
          const url = hm?.url || selectedUrl || preview
          return (
            <Panel key={heatmapId || `unknown-${idx}`}>
              <ImageWrapper $width={displayWidth}>
                {(loading || isLoading || imgLoading) && <StyledLoading />}
                {url ? (
                  <>
                    <div
                      onClick={() =>
                        handleClickOpen(
                          url,
                          coords,
                          hm?.name || hm?.fileName || t('heatmap')
                        )
                      }
                      style={{
                        display: 'flex',
                        margin: '0 auto',
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                    >
                      <SmallImgPreview
                        src={url}
                        alt={t('preview')}
                        style={{
                          position: 'relative',
                          width: displayWidth,
                          height: displayHeight
                        }}
                      />
                      <RenderCoordinates
                        coordinates={coords}
                        cellSize={cellSize}
                      />
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                      <strong>
                        {hm?.name || hm?.fileName || t('heatmap')}
                      </strong>
                      {` — ${t('totalInjuries')}: ${count}`}
                    </div>
                  </>
                ) : (
                  <NoImageBox>
                    <span>{t('noImage')}</span>
                  </NoImageBox>
                )}
              </ImageWrapper>
            </Panel>
          )
        })}
      </ScrollRow>
    </div>
  )
}

const mapStateToProps = (state: ApplicationState) => ({
  resort: state.resort.data,
  isLoading: state.resort.loading
})

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(ResortActions, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(Heatmap)
